import React, { useEffect, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Switch,
	TextField,
	Typography
} from "@mui/material";
import { ContentCopy, Language } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";

// Chat do site (webchat): criar/editar a conexão e copiar o snippet do widget.
// Backend: /webchat-admin (controllers/WebchatAdminController.ts).

const DIAS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const PADRAO = {
	title: "Fale conosco",
	subtitle: "Respondemos por aqui mesmo",
	logoUrl: "",
	position: "right",
	welcomeMessage: "Olá! 👋 Como podemos ajudar?",
	offHoursMessage:
		"Estamos fora do horário de atendimento agora. Deixe sua mensagem que respondemos assim que voltarmos.",
	offHoursAllowMessages: true,
	businessHours: {
		enabled: false,
		timezone: "America/Sao_Paulo",
		days: { 1: [["08:00", "18:00"]], 2: [["08:00", "18:00"]], 3: [["08:00", "18:00"]], 4: [["08:00", "18:00"]], 5: [["08:00", "18:00"]] }
	},
	allowedOrigins: [],
	askEmail: "optional",
	askPhone: "optional",
	lgpdText:
		"Ao iniciar a conversa, você concorda que usaremos seu nome e contato apenas para responder ao seu atendimento, conforme a LGPD.",
	privacyUrl: "",
	whatsappNumber: "",
	whatsappText: "Olá! Vim pelo site.",
	maxFileMb: 10,
	botWebhookUrl: "",
	botSecret: ""
};

const WebchatModal = ({ open, onClose, whatsAppId }) => {
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [name, setName] = useState("Chat do site");
	const [color, setColor] = useState("#0B5FA5");
	const [enabled, setEnabled] = useState(true);
	const [queueIds, setQueueIds] = useState([]);
	const [cfg, setCfg] = useState(PADRAO);
	const [origins, setOrigins] = useState("");
	const [snippet, setSnippet] = useState("");
	const [savedId, setSavedId] = useState(null);

	const id = whatsAppId || savedId;

	useEffect(() => {
		if (!open) return;
		setSavedId(null);
		if (!whatsAppId) {
			setName("Chat do site");
			setColor("#0B5FA5");
			setEnabled(true);
			setQueueIds([]);
			setCfg(PADRAO);
			setOrigins("");
			setSnippet("");
			return;
		}
		setLoading(true);
		api
			.get(`/webchat-admin/${whatsAppId}`)
			.then(({ data }) => carregar(data))
			.catch(err => toastError(err))
			.finally(() => setLoading(false));
	}, [open, whatsAppId]);

	const carregar = data => {
		setName(data.name);
		setColor(data.color || "#0B5FA5");
		setEnabled(data.status !== "DISABLED");
		setQueueIds(data.queueIds || []);
		setCfg({ ...PADRAO, ...data.config, botSecret: "" });
		setOrigins((data.config.allowedOrigins || []).join("\n"));
		setSnippet(data.snippet);
	};

	const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));
	const setBH = (k, v) => setCfg(c => ({ ...c, businessHours: { ...c.businessHours, [k]: v } }));
	const setDia = (d, ativo, ini, fim) =>
		setCfg(c => {
			const days = { ...c.businessHours.days };
			if (ativo) days[d] = [[ini || "08:00", fim || "18:00"]];
			else delete days[d];
			return { ...c, businessHours: { ...c.businessHours, days } };
		});

	const salvar = async () => {
		const allowedOrigins = origins
			.split(/[\s,]+/)
			.map(o => o.trim())
			.filter(Boolean);
		if (!allowedOrigins.length) {
			toast.warn("Informe pelo menos um domínio permitido (ex.: https://www.sabesistemas.com.br).");
			return;
		}
		setSaving(true);
		try {
			const body = {
				name,
				color,
				queueIds,
				status: enabled ? "CONNECTED" : "DISABLED",
				config: { ...cfg, allowedOrigins }
			};
			const { data } = id
				? await api.put(`/webchat-admin/${id}`, body)
				: await api.post(`/webchat-admin`, body);
			carregar(data);
			setSavedId(data.id);
			toast.success("Chat do site salvo. Copie o código abaixo e cole no site.");
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const novoToken = async () => {
		if (!id || !window.confirm("Gerar um token novo? O código instalado no site para de funcionar até ser trocado.")) return;
		try {
			const { data } = await api.post(`/webchat-admin/${id}/token`);
			carregar(data);
			toast.success("Token novo gerado.");
		} catch (err) {
			toastError(err);
		}
	};

	const copiar = () => {
		navigator.clipboard
			.writeText(snippet)
			.then(() => toast.success("Código copiado!"))
			.catch(() => toast.info("Selecione o código e copie com Ctrl+C."));
	};

	const modoCampo = (k, label) => (
		<FormControl fullWidth size="small">
			<InputLabel>{label}</InputLabel>
			<Select label={label} value={cfg[k]} onChange={e => set(k, e.target.value)}>
				<MenuItem value="off">Não pedir</MenuItem>
				<MenuItem value="optional">Opcional</MenuItem>
				<MenuItem value="required">Obrigatório</MenuItem>
			</Select>
		</FormControl>
	);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
			<DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<Language color="primary" /> {id ? "Editar chat do site" : "Novo chat do site"}
			</DialogTitle>
			<DialogContent dividers>
				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
						<CircularProgress />
					</Box>
				) : (
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<TextField fullWidth size="small" label="Nome da conexão" value={name} onChange={e => setName(e.target.value)} />
						</Grid>
						<Grid item xs={6} sm={3}>
							<TextField fullWidth size="small" type="color" label="Cor" value={color} onChange={e => setColor(e.target.value)} />
						</Grid>
						<Grid item xs={6} sm={3}>
							<FormControlLabel control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} />} label={enabled ? "Ativo" : "Desativado"} />
						</Grid>

						<Grid item xs={12}>
							<TextField
								fullWidth
								size="small"
								multiline
								minRows={2}
								label="Domínios permitidos (um por linha)"
								placeholder={"https://www.sabesistemas.com.br\nhttps://sabesistemas.com.br"}
								helperText="Só páginas destes endereços conseguem abrir o chat. Aceita *.dominio.com.br para subdomínios."
								value={origins}
								onChange={e => setOrigins(e.target.value)}
							/>
						</Grid>

						<Grid item xs={12}>
							<QueueSelect selectedQueueIds={queueIds} onChange={setQueueIds} />
							<Typography variant="caption" color="text.secondary">
								Com 1 setor, o ticket já nasce nele. Com vários, vale a URA (se ligada nesta instância).
							</Typography>
						</Grid>

						<Grid item xs={12}><Divider>Aparência</Divider></Grid>
						<Grid item xs={12} sm={6}>
							<TextField fullWidth size="small" label="Título do balão" value={cfg.title} onChange={e => set("title", e.target.value)} />
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField fullWidth size="small" label="Subtítulo (online)" value={cfg.subtitle} onChange={e => set("subtitle", e.target.value)} />
						</Grid>
						<Grid item xs={12} sm={8}>
							<TextField fullWidth size="small" label="URL do logo (https://...)" value={cfg.logoUrl} onChange={e => set("logoUrl", e.target.value)} />
						</Grid>
						<Grid item xs={12} sm={4}>
							<FormControl fullWidth size="small">
								<InputLabel>Posição</InputLabel>
								<Select label="Posição" value={cfg.position} onChange={e => set("position", e.target.value)}>
									<MenuItem value="right">Canto direito</MenuItem>
									<MenuItem value="left">Canto esquerdo</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12}>
							<TextField fullWidth size="small" multiline minRows={2} label="Mensagem de boas-vindas" value={cfg.welcomeMessage} onChange={e => set("welcomeMessage", e.target.value)} />
						</Grid>

						<Grid item xs={12}><Divider>Formulário e LGPD</Divider></Grid>
						<Grid item xs={12} sm={6}>{modoCampo("askEmail", "E-mail")}</Grid>
						<Grid item xs={12} sm={6}>{modoCampo("askPhone", "Telefone")}</Grid>
						<Grid item xs={12}>
							<TextField fullWidth size="small" multiline minRows={2} label="Aviso de privacidade (o visitante marca antes de iniciar)" value={cfg.lgpdText} onChange={e => set("lgpdText", e.target.value)} />
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField fullWidth size="small" label="Link da política de privacidade" value={cfg.privacyUrl} onChange={e => set("privacyUrl", e.target.value)} />
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField fullWidth size="small" type="number" label="Tamanho máx. do anexo (MB, até 25)" value={cfg.maxFileMb} onChange={e => set("maxFileMb", e.target.value)} />
						</Grid>

						<Grid item xs={12}><Divider>Horário de atendimento</Divider></Grid>
						<Grid item xs={12} sm={6}>
							<FormControlLabel control={<Switch checked={!!cfg.businessHours.enabled} onChange={e => setBH("enabled", e.target.checked)} />} label="Usar horário de atendimento" />
						</Grid>
						<Grid item xs={12} sm={6}>
							<FormControlLabel
								control={<Checkbox checked={!!cfg.offHoursAllowMessages} onChange={e => set("offHoursAllowMessages", e.target.checked)} />}
								label="Fora do horário, deixar o visitante mandar recado"
							/>
						</Grid>
						{cfg.businessHours.enabled &&
							DIAS.map((dia, i) => {
								const faixa = (cfg.businessHours.days[i] || [])[0];
								return (
									<Grid item xs={12} sm={6} md={4} key={dia} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Checkbox size="small" checked={!!faixa} onChange={e => setDia(i, e.target.checked)} />
										<Typography sx={{ width: 32 }}>{dia}</Typography>
										<TextField size="small" type="time" disabled={!faixa} value={faixa ? faixa[0] : ""} onChange={e => setDia(i, true, e.target.value, faixa[1])} />
										<TextField size="small" type="time" disabled={!faixa} value={faixa ? faixa[1] : ""} onChange={e => setDia(i, true, faixa[0], e.target.value)} />
									</Grid>
								);
							})}
						<Grid item xs={12}>
							<TextField fullWidth size="small" multiline minRows={2} label="Mensagem fora do horário" value={cfg.offHoursMessage} onChange={e => set("offHoursMessage", e.target.value)} />
						</Grid>

						<Grid item xs={12}><Divider>WhatsApp e bot (opcionais)</Divider></Grid>
						<Grid item xs={12} sm={5}>
							<TextField fullWidth size="small" label="Nº do WhatsApp (55DDDNÚMERO)" helperText='Mostra "Continuar pelo WhatsApp"' value={cfg.whatsappNumber} onChange={e => set("whatsappNumber", e.target.value)} />
						</Grid>
						<Grid item xs={12} sm={7}>
							<TextField fullWidth size="small" label="Texto inicial no WhatsApp" value={cfg.whatsappText} onChange={e => set("whatsappText", e.target.value)} />
						</Grid>
						<Grid item xs={12} sm={7}>
							<TextField fullWidth size="small" label="Webhook do bot / n8n (https://...)" value={cfg.botWebhookUrl} onChange={e => set("botWebhookUrl", e.target.value)} />
						</Grid>
						<Grid item xs={12} sm={5}>
							<TextField fullWidth size="small" type="password" label="Segredo do bot" helperText="Em branco = mantém o atual" value={cfg.botSecret} onChange={e => set("botSecret", e.target.value)} />
						</Grid>

						{snippet && (
							<>
								<Grid item xs={12}><Divider>Instalação no site</Divider></Grid>
								<Grid item xs={12}>
									<Typography variant="body2" sx={{ mb: 1 }}>
										Cole este código antes de <code>&lt;/body&gt;</code> em todas as páginas do site (no WordPress: plugin de "inserir cabeçalho e rodapé" ou no tema).
									</Typography>
									<TextField fullWidth multiline size="small" value={snippet} InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 13 } }} />
									<Box sx={{ display: "flex", gap: 1, mt: 1 }}>
										<Button variant="contained" startIcon={<ContentCopy />} onClick={copiar}>Copiar código</Button>
										<Button color="warning" onClick={novoToken}>Gerar token novo</Button>
									</Box>
								</Grid>
							</>
						)}
					</Grid>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Fechar</Button>
				<Button variant="contained" onClick={salvar} disabled={saving || loading}>
					{saving ? <CircularProgress size={20} /> : "Salvar"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default WebchatModal;
